import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useQuery } from '@tanstack/react-query';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import type { MemberOnboardingData } from '../index';
import { InternalSubjectSurveys } from '../../PatientDetail/ModuleTypes/SubjectSurveys';
// @ts-expect-error, importing javascript module
import SubjectConsent from '../../PatientDetail/ModuleTypes/SubjectConsent';
import { MemberAppointments } from './MemberAppointments';
import { OnboardingSnapshotList } from './OnboardingSnapshotList';
import clsx from 'clsx';
import { MemberInfo } from '../../../workflows/MemberInfo';
import { useActiveProjectCohortId } from '@lifeomic/phc-web-toolkit/src/hooks';
import type {
  PlatformDefaultBundle,
  SearchParamsForResourceType,
  WithPlatformDefaults,
} from '@lifeomic/service-clients/fhir-client';
import { fhirClient, useDataset } from '../../../../util/api';
import {
  getAppUsernameFromPersonSearchBundle,
  MEMBER_APP_PERSON_USERNAME_QUERY_KEY,
} from '../../../workflows/memberAppUsername';

const messages = defineMessages({
  title: {
    id: 'IndividualOnboardingView.title',
    defaultMessage: 'Onboarding Details',
  },
  memberInfo: {
    id: 'IndividualOnboardingView.memberInfo',
    defaultMessage: 'Member Info',
  },
  forms: {
    id: 'IndividualOnboardingView.forms',
    defaultMessage: 'Forms',
  },
  consents: {
    id: 'IndividualOnboardingView.consents',
    defaultMessage: 'Consents',
  },
  appointments: {
    id: 'IndividualOnboardingView.appointments',
    defaultMessage: 'Appointments',
  },
  elationId: {
    id: 'IndividualOnboardingView.elationId',
    defaultMessage: 'Elation ID',
  },
  copyToClipboard: {
    id: 'IndividualOnboardingView.copyToClipboard',
    defaultMessage: 'Copy to clipboard',
  },
});

const useStyles = makeStyles(theme => ({
  container: {
    display: 'grid',
    gridTemplateColumns: '1fr 3fr',
  },
  surveys: {
    '& div[role="tabpanel"]': {
      paddingLeft: 0,
      paddingRight: 0,
      backgroundColor: 'transparent',
      flex: 0,
    },
  },
  boxShadow: {
    boxShadow: theme.boxShadows.table,
  },
  overflow: {
    overflowY: 'auto',
    '& > div': {
      width: '100%',
    },
    '& table': {
      boxShadow: theme.boxShadows.table,
    },
  },
}));

type IndividualOnboardingViewProps = MemberOnboardingData & {
  onMemberContactSaved?: () => Promise<void>;
};

export const IndividualOnboardingView: React.FC<
  IndividualOnboardingViewProps
> = props => {
  const { formatMessage } = useIntl();
  const styles = useStyles();
  const dataset = useDataset();
  const cohortId = useActiveProjectCohortId();

  const personUsernameQuery = useQuery({
    queryKey: [
      ...MEMBER_APP_PERSON_USERNAME_QUERY_KEY,
      props.patient.id,
      dataset?.id,
      cohortId,
    ],
    queryFn: async (): Promise<
      PlatformDefaultBundle<WithPlatformDefaults<fhir3.Person>>
    > => {
      const response = await fhirClient.request('GET /v1/fhir/dstu3/Person', {
        identifier: 'http://lifeomic.com/fhir/username|',
        patient: props.patient.id,
        _tag: dataset.codingToken,
        ...(cohortId ? { cohort: cohortId } : {}),
        pageSize: 1,
      } as SearchParamsForResourceType<'Person'> & {
        identifier: string;
        cohort?: string;
      });
      return response.data;
    },
    enabled: Boolean(props.patient?.id && dataset?.id),
  });

  const appUsername = React.useMemo(
    () => getAppUsernameFromPersonSearchBundle(personUsernameQuery.data),
    [personUsernameQuery.data],
  );

  return (
    <Box className={styles.container} direction="column" gap={4}>
      <Box direction="column">
        <MemberInfo
          member={props.member}
          patient={props.patient}
          memberOpsEnabled
          onMemberContactSaved={props.onMemberContactSaved}
          appUsername={appUsername}
          isAppUsernameLoading={personUsernameQuery.isLoading}
        />
        <OnboardingSnapshotList
          member={props.member}
          snapshots={props.snapshots}
        />
      </Box>

      <Box direction="column" gap={4}>
        <Box direction="column" gap={1}>
          <Text weight="bold">{formatMessage(messages.appointments)}</Text>
          <Box className={clsx(styles.overflow, styles.boxShadow)}>
            <MemberAppointments patientId={props.patient.id} />
          </Box>
        </Box>
        <Box direction="column" gap={1}>
          <Text weight="bold">{formatMessage(messages.consents)}</Text>
          <Box className={clsx(styles.overflow, styles.boxShadow)}>
            <SubjectConsent patient={props.patient} />
          </Box>
        </Box>
        <Box direction="column" gap={1} className={styles.surveys}>
          <Text weight="bold">{formatMessage(messages.forms)}</Text>
          <Box className={clsx(styles.overflow, styles.boxShadow)}>
            <InternalSubjectSurveys
              patient={props.patient}
              defaultTab="responses"
              defaultStatusFilter="all"
              applyDateFilter={false}
              dateTickFilter={{ max: 1, min: 1 }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

type IndividualOnboardingModalProps = {
  isOpen: boolean;
  onDismiss: () => void;
};

export const IndividualOnboardingModal: React.FC<
  IndividualOnboardingModalProps & { detail: MemberOnboardingData | null }
> = ({ isOpen, onDismiss, detail }) => {
  const { formatMessage } = useIntl();

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={onDismiss}
      title={formatMessage(messages.title)}
      isFullScreen
    >
      {detail ? <IndividualOnboardingView {...detail} /> : null}
    </Modal>
  );
};
